import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { doAddEntry } from '@databases/redux/table-layout/actions';
import { DatabaseEntry } from '@databases/types/database';
import { HALF_ROW_SELECT_BUTTON_WIDTH } from '@databases/utils/constants';
import useView from '@databases/views/useView';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';

const StyledButton = styled(Button)`
  border-radius: 0;
  && {
    font-size: 12px;
    align-items: center;
    height: 100%;
    text-align: left;
  }

  svg {
    height: 18px;
    width: auto;
  }
`;

const addEntryContainer = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderBottomLeftRadius: '3px',
  borderBottomRightRadius: '3px',
  borderColor: 'color.border',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

export interface AddEntryButtonProps {
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  contentId: string;
  onAdd?: (entry: DatabaseEntry) => void;
  width: number | undefined;
}

const AddEntryButton: FunctionComponent<AddEntryButtonProps> = ({
  addEntryButtonRef,
  contentId,
  onAdd,
  width,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const {
    view: { settings: viewSettings },
  } = useView();

  const { formatMessage } = useIntl();

  const scope = useScope();
  const handleClick = useFlaggingCallback(async () => {
    dispatch(deselectAll());
    await client.sendAddEntryButtonClickedEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
    });
    const entry = dispatch(doAddEntry(contentId, scope, viewSettings));
    onAdd?.(entry);
  }, [client, contentId, dispatch, onAdd, scope, viewSettings]);

  const stickyStyles = xcss({
    position: 'sticky',
    bottom: 'space.0',
    left: 'space.0',
    zIndex: 'card',
    backgroundColor: 'elevation.surface',
  });

  return (
    <Flex xcss={[stickyStyles, xcss({ width: '100%' })]}>
      <Box
        xcss={[
          stickyStyles,
          xcss({ width: `${HALF_ROW_SELECT_BUTTON_WIDTH}px`, height: '40px' }),
        ]}
      />
      <Box
        xcss={addEntryContainer}
        style={width ? { width } : { marginRight: '1px', width: '100%' }}
      >
        <StyledButton
          ref={addEntryButtonRef}
          appearance="subtle"
          onClick={handleClick}
          iconBefore={<AddIcon label="" />}
          spacing="compact"
          shouldFitContainer
        >
          {formatMessage(messages['table-layout.add-entry'])}
        </StyledButton>
      </Box>
    </Flex>
  );
};

export default AddEntryButton;
